import { faExclamationTriangle, faEye, faPen, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { PopoverProps } from "../Popover";
import Tooltip from "../Tooltip";

// https://mui.com/material-ui/react-text-field/
// https://www.w3.org/WAI/tutorials/forms/labels/

export const TextFieldState = {
  default:
    "bg-white border-vpGray-300 focus:border-vpBlue-500 focus:ring-vpBlue-500 focus:ring-1 hover:border-vpBlue-500 outline-0",
  disabled: "border-vpGray-300 bg-vpGray-200 placeholder:text-vpGray-500 text-vpGray-500 cursor-default",
  error:
    "bg-vpRed-100 border-vpRed-500 focus:ring-vpRed-500 focus:border-vpRed-500 focus:ring-1 hover:border-vpRed-500 outline-0",
} as const;

export const TextFieldSize = {
  small: "px-3 py-2 text-md",
  medium: "px-3 py-2 text-lg",
  large: "px-3 py-3 text-xl",
} as const;

type Omits = "onBlur" | "onChange" | "onFocus" | "onKeyDown";

export interface TextFieldProps extends Omit<React.HTMLAttributes<HTMLDivElement>, Omits> {
  "aria-label"?: string;
  clearable?: boolean;
  counter?: boolean;
  dataCy?: string;
  disabled?: boolean;
  defaultValue?: string;
  error?: boolean;
  errorMessage?: React.ReactNode;
  errorNode?: React.ReactNode;
  errorTooltipProps?: PopoverProps;
  inputProps?: React.HTMLAttributes<HTMLInputElement | HTMLTextAreaElement>;
  label?: string;
  lockable?: boolean;
  max?: number;
  multiline?: boolean;
  name?: string;
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onClear?: () => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  placeholder?: string;
  size?: keyof typeof TextFieldSize;
  slots?: {
    input?: {
      css: string;
    };
    startNode?: {
      css?: string;
    };
  };
  startNode?: React.ReactNode;
  type?: string;
  value?: string;
}

function TextField({
  "aria-label": ariaLabel,
  clearable,
  counter,
  dataCy,
  disabled: disabledProp,
  defaultValue,
  error,
  errorMessage,
  errorNode,
  errorTooltipProps,
  id,
  inputProps: { className: inputPropsClassName, ...inputProps } = {},
  label,
  lockable,
  onBlur,
  onChange,
  onClear,
  onFocus,
  onKeyDown,
  max,
  multiline,
  name,
  placeholder,
  size = "medium",
  slots,
  startNode,
  type,
  value,
  ...props
}: TextFieldProps) {
  const [disabled, setDisabled] = useState(disabledProp);
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDisabled(disabledProp);
  }, [disabledProp]);

  useEffect(() => {
    const isFocused = document.activeElement === inputRef.current;

    if (lockable && !isFocused) {
      setDisabled(true);
    }
  }, [lockable]);

  const handleBlur: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement> = (event) => {
    const text = event.target.value.trim();

    onBlur?.(event);

    if (text && lockable) {
      setDisabled(true);
    }
  };

  const hasErrorIcon = Boolean(error);

  return (
    <div {...props}>
      {label && (
        <label className="block text-sm font-medium" htmlFor={id}>
          {label}
        </label>
      )}
      <div
        className={classNames("relative flex items-center rounded-md", {
          "mt-1": label,
        })}
      >
        <div className="absolute left-1 flex">
          {startNode ? (
            <span
              aria-label="Start icon"
              className={twMerge(
                classNames(
                  "p-2",
                  {
                    "text-vpGray-500": disabled,
                  },
                  slots?.startNode?.css
                )
              )}
            >
              {startNode}
            </span>
          ) : null}
        </div>
        <div className="absolute right-0 flex h-full">
          {type === "password" && !disabled && (
            <span
              aria-label={isPasswordVisible ? "Hide password" : "Show password"}
              className="inline-flex cursor-pointer items-center p-4 text-vpGray-600 transition hover:text-vpBlue-500"
              onClick={() => {
                setIsPasswordVisible(!isPasswordVisible);
              }}
            >
              <FontAwesomeIcon icon={isPasswordVisible ? faEye : faEye} />
            </span>
          )}
          {value && lockable && disabled && (
            <span
              aria-label="Locked"
              className="inline-flex cursor-pointer items-center p-4 text-vpGray-600 transition hover:text-vpBlue-500"
              onClick={() => {
                setDisabled(false);

                setTimeout(() => inputRef.current?.focus(), 200);
              }}
            >
              <FontAwesomeIcon icon={faPen} />
            </span>
          )}
          {value && clearable && (
            <span
              aria-label="Clear"
              className="inline-flex cursor-pointer items-center p-4 text-vpGray-600 transition hover:text-vpBlue-500"
              onClick={() => {
                if (defaultValue && inputRef.current) {
                  inputRef.current.value = "";
                } else {
                  onClear?.();
                }
              }}
            >
              <FontAwesomeIcon icon={faXmark} />
            </span>
          )}
          {hasErrorIcon && (
            <div aria-label="Error" className="inline-flex items-center justify-center p-4 transition">
              <Tooltip content={errorMessage} {...errorTooltipProps}>
                <span className="inline-flex">
                  {errorNode ? errorNode : <FontAwesomeIcon className="text-vpRed-500" icon={faExclamationTriangle} />}
                </span>
              </Tooltip>
            </div>
          )}
        </div>
        {multiline ? (
          <textarea
            aria-label={ariaLabel}
            autoComplete="off"
            className={twMerge(
              classNames(
                "md:text-md w-full flex-1 rounded-md border text-sm text-black transition",
                inputPropsClassName,
                TextFieldState[error ? "error" : disabled ? "disabled" : "default"],
                TextFieldSize[size],
                slots?.input?.css
              )
            )}
            defaultValue={defaultValue}
            disabled={disabled}
            id={id}
            onBlur={handleBlur}
            onChange={onChange}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            name={name}
            placeholder={placeholder}
            rows={4}
            value={value}
            {...inputProps}
          />
        ) : (
          <input
            autoComplete="off"
            aria-label={ariaLabel}
            className={twMerge(
              classNames(
                "md:text-md w-full flex-1 rounded-md border text-sm text-black transition",
                inputPropsClassName,
                TextFieldState[error ? "error" : disabled ? "disabled" : "default"],
                TextFieldSize[size],
                {
                  "pl-10": startNode,
                  "pr-10": hasErrorIcon,
                },
                slots?.input?.css
              )
            )}
            data-cy={dataCy}
            disabled={disabled}
            defaultValue={defaultValue}
            id={id}
            max={max}
            name={name}
            onBlur={handleBlur}
            onChange={onChange}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            ref={inputRef}
            type={type === "password" ? (isPasswordVisible ? "text" : "password") : type}
            value={value}
            {...inputProps}
          />
        )}
      </div>
      {max && counter && (
        <div
          className={classNames("text-right text-sm", {
            "text-vpRed-500": value?.length === max,
          })}
        >
          {`${value?.length}/${max}`}
        </div>
      )}
    </div>
  );
}

export default TextField;
